import { useMemo } from 'react';

import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@mesh.ai/rabbit';
import { LabelFieldWrapper } from 'screens/admin/Reviews/ReviewProcessV2Intent/components/LabelFieldWrapper';

import {
  ENGAGEMENT_SCALE_TYPE_OPTIONS,
  MAX_OPTIONS_FOR_NPS,
  NPS_SCALE_OPTIONS,
  SCALE_VALUE_OPTIONS,
} from '../constants/AddQuestionConstant';
import { generateEngagementScaleOptions } from '../helpers';
import { EngagementQuestionType, useAddEngagementQuestion } from '../slice/AddEngagementQuestion.slice';

type Props = {
  handleQuestionChange: (value: Partial<EngagementQuestionType>) => void;
};

const ScaleValueDropdown = ({ handleQuestionChange }: Props) => {
  const { answerType, maxOptions, scaleType, answerOptions } = useAddEngagementQuestion();
  const scaleOptions = useMemo(() => SCALE_VALUE_OPTIONS[answerType], [answerType]);

  return (
    <LabelFieldWrapper label="Scale value" gridTemplateColumns="200px 1fr" gridGap={0}>
      <Select
        value={maxOptions.toString()}
        onValueChange={(value) => {
          // NPS
          if (answerType === 'NUMBER_RATING' && value === String(MAX_OPTIONS_FOR_NPS)) {
            handleQuestionChange({
              maxOptions: +value,
              answerOptions: NPS_SCALE_OPTIONS,
            });
            return;
          }
          // is choice based
          if (answerType === 'DROP_DOWN') {
            handleQuestionChange({
              maxOptions: +value,
              answerOptions: generateEngagementScaleOptions(+value),
            });
            return;
          }
          // is emoji based or scale based
          // if scaleType === null, i.e. custom, then set remove or add empty objects to existing answerOptions
          // if not, then set the answerOptions to the selected scaleType
          if (!scaleType) {
            const existingOptions = answerOptions;
            const newOptions = Array.from({ length: +value }, (_, i) => ({
              score: i + 1,
              description: existingOptions[i]?.description ?? '',
              displayOption: i,
              isDisabled: false,
            }));

            handleQuestionChange({
              maxOptions: +value,
              answerOptions: newOptions,
            });
          } else {
            handleQuestionChange({
              maxOptions: +value,
              answerOptions: ENGAGEMENT_SCALE_TYPE_OPTIONS[scaleType][+value],
            });
          }
        }}
      >
        <SelectTrigger style={{ width: '100%', textAlign: 'left' }}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent style={{ maxWidth: '100%' }}>
          <SelectGroup>
            {scaleOptions.map(({ value, label }) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </LabelFieldWrapper>
  );
};

export { ScaleValueDropdown };
